import React, { useState, useRef, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import SkillsInput from './SkillsInput';
import MonthPicker from './MonthPicker';
import { api } from '../../../../utils/api';
import { API_ENDPOINTS, S3_BASE_URL as S3_BASE_URL_RAW } from '../../../../config';
import '../../../../styles/profile/components/add-project-modal.css';

interface AddProjectModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: ProjectData) => void;
  title?: string;
  initialData?: any | null;
  isEditMode?: boolean;
  isLoading?: boolean;
}

interface ProjectData {
  uid?: string;
  title: string;
  headline: string;
  description: string;
  releaseDate: string;
  achievements: string[];
  link: string;
  skills: string[];
  gallery: File[];
  existingImages?: string[];
}

export default function AddProjectModal({ 
  open, 
  onClose, 
  onAdd,
  title: modalTitle = "Add Project",
  initialData = null,
  isEditMode = false,
  isLoading = false
}: AddProjectModalProps) {
  const [title, setTitle] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState<Date | null>(null);
  const [showReleaseCalendar, setShowReleaseCalendar] = useState(false);
  const [achievements, setAchievements] = useState<string[]>([]);
  const [link, setLink] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [gallery, setGallery] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  
  const overlayRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const S3_BASE_URL = S3_BASE_URL_RAW.replace(/\/$/, '');

  useEffect(() => {
    if (open && initialData) {
      setTitle(initialData.title || '');
      setHeadline(initialData.headline || '');
      setDescription(initialData.description || '');
      setReleaseDate(initialData.releaseDate ? new Date(initialData.releaseDate) : null);
      setLink(initialData.link || '');
      setSkills(initialData.skills || []);
      setAchievements(initialData.achievements || []);
      setExistingImages(initialData.existingGalleryUrls || []);
      setGallery([]);
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setHeadline('');
      setDescription('');
      setReleaseDate(null);
      setShowReleaseCalendar(false);
      setAchievements([]);
      setLink('');
      setSkills([]);
      setGallery([]);
      setExistingImages([]);
    }
  }, [open]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ release date';
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const handleSubmit = () => {
    if (!title.trim() || !headline.trim()) {
      return;
    }

    const data: ProjectData = {
      title,
      headline,
      description,
      releaseDate: releaseDate ? releaseDate.toISOString() : '',
      achievements: achievements.filter(a => a.trim() !== ''),
      link,
      skills,
      gallery,
      existingImages,
    };

    if (isEditMode && initialData?.uid) {
      data.uid = initialData.uid;
    }

    onAdd(data);
  };

  const handleAddAchievement = () => {
    setAchievements([...achievements, '']);
  };

  const handleRemoveAchievement = (index: number) => {
    setAchievements(achievements.filter((_, i) => i !== index));
  };

  const handleAchievementChange = (index: number, value: string) => {
    const newAchievements = [...achievements];
    newAchievements[index] = value;
    setAchievements(newAchievements);
  };

  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const imageFiles = Array.from(files).filter(file => 
        file.type === 'image/jpeg' || file.type === 'image/png'
      );
      setGallery([...gallery, ...imageFiles]);
    }
  };

  const handleRemoveImage = (index: number) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = async (index: number, imgUrl: string) => {
    if (isEditMode && initialData?.uid) {
      try {
        await api.delete(API_ENDPOINTS.users.deleteProjectImage(initialData.uid, imgUrl));
      } catch (error) {
      }
    }
    
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <div
      className="add-project-modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="add-project-modal">
        <div className="add-project-modal-header">
          <h2 className="add-project-modal-title">{modalTitle}</h2>
        </div>

        <div className="add-project-modal-body">
          <div className="add-project-modal-grid">
            <div className="add-project-modal-column">
              <div className="add-project-modal-field">
                <label className="add-project-modal-label">
                  Title
                  <span className="add-project-modal-required">Required</span>
                </label>
                <input
                  type="text"
                  className="add-project-modal-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">
                  Headline
                  <span className="add-project-modal-required">Required</span>
                </label>
                <input
                  type="text"
                  className="add-project-modal-input"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                />
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Description</label>
                <textarea
                  className="add-project-modal-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Release Date</label>
                <div className="add-project-modal-date-wrapper">
                  <button
                    type="button"
                    className="add-project-modal-date-btn"
                    style={{ color: releaseDate ? '#1f2937' : '#9ca3af' }}
                    onClick={() => setShowReleaseCalendar(!showReleaseCalendar)}
                  >
                    {formatDate(releaseDate)}
                  </button>
                  {showReleaseCalendar && (
                    <MonthPicker
                      selectedDate={releaseDate}
                      onSelect={(date) => {
                        setReleaseDate(date);
                        setShowReleaseCalendar(false);
                      }}
                      onClose={() => setShowReleaseCalendar(false)}
                    />
                  )}
                </div>
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Achievements</label>
                {achievements.map((achievement, index) => (
                  <div key={index} className="add-project-modal-achievement-item">
                    <input
                      type="text"
                      className="add-project-modal-input"
                      placeholder="Achievement"
                      value={achievement}
                      onChange={(e) => handleAchievementChange(index, e.target.value)}
                    />
                    <button
                      type="button"
                      className="add-project-modal-remove-btn"
                      onClick={() => handleRemoveAchievement(index)}
                      aria-label="Remove achievement"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="add-project-modal-add-achievement"
                  onClick={handleAddAchievement}
                >
                  <span className="add-project-modal-add-icon">+</span>
                </button>
              </div>
            </div>

            <div className="add-project-modal-column">
              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Link</label>
                <input
                  type="text"
                  className="add-project-modal-input"
                  placeholder="+ add URL"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Skills</label>
                <SkillsInput skills={skills} onChange={setSkills} placeholder="Select Skills" />
              </div>

              <div className="add-project-modal-field">
                <label className="add-project-modal-label">Gallery</label>
                <div className="add-project-modal-gallery">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  {gallery.length === 0 && existingImages.length === 0 ? (
                    <button
                      type="button"
                      className="add-project-modal-gallery-upload"
                      onClick={handleGalleryClick}
                    >
                      <div className="add-project-modal-gallery-icon">☁</div>
                      <div className="add-project-modal-gallery-text">jpg, png</div>
                    </button>
                  ) : (
                    <div className="add-project-modal-gallery-preview">
                      {existingImages.map((url, index) => (
                        <div key={`existing-${index}`} className="add-project-modal-gallery-item">
                          <img
                            src={`${S3_BASE_URL}/${url}`}
                            alt={`Existing ${index + 1}`}
                            className="add-project-modal-gallery-img"
                          />
                          <button
                            type="button"
                            className="add-project-modal-gallery-remove"
                            onClick={() => handleRemoveExistingImage(index, url)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {gallery.map((file, index) => (
                        <div key={`new-${index}`} className="add-project-modal-gallery-item">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="add-project-modal-gallery-img"
                          />
                          <button
                            type="button"
                            className="add-project-modal-gallery-remove"
                            onClick={() => handleRemoveImage(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="add-project-modal-gallery-add-more"
                        onClick={handleGalleryClick}
                      >
                        <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>+</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="add-project-modal-footer">
          <button
            type="button"
            className="add-project-modal-btn add-project-modal-btn--discard"
            onClick={onClose}
          >
            Discard
          </button>
          <button
            type="button"
            className="add-project-modal-btn add-project-modal-btn--add"
            onClick={handleSubmit}
            disabled={isLoading}
            style={{ opacity: isLoading ? 0.6 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
          >
            {isLoading ? 'Saving...' : (isEditMode ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

